import { useParams } from 'react-router';
import { DataTable } from '../components/DataTable.tsx';
import { ErrorState, KeyValueCard, LoadingState, StatusChip } from '../components/FdPrimitives.tsx';
import { useFetch } from '../hooks/useFetch.ts';

type DeviceEvent = {
  event_id: string;
  site_key: string;
  ip: string;
  event_name: string;
  ts: string;
  server_ts: string;
};

type DeviceDecision = {
  request_id: string;
  order_id: string;
  arm: string;
  recommendation: string;
  score: number;
  ts: string;
};

type ServerEvent = {
  order_id: string;
  request_id: string;
  event_name: string;
  ts: string;
};

type DeviceDetail = {
  alias: string;
  device_id: string;
  site_key: string;
  first_seen: string;
  last_seen: string;
  event_count: number;
  ip_count: number;
  recent_events: DeviceEvent[];
  recent_decisions: DeviceDecision[];
  server_events: ServerEvent[];
};

export function DeviceDetailPage() {
  const { alias } = useParams();
  const state = useFetch<{ device: DeviceDetail }>(`/devices/${encodeURIComponent(alias || '')}`);

  if (state.status === 'loading') return <LoadingState label="Loading device" />;
  if (state.status === 'error') return <ErrorState message={state.message} />;

  const { device } = state.data;

  return (
    <div className="page-stack">
      <div className="page-head">
        <div>
          <h2>Device {device.alias || alias}</h2>
          <p>{device.event_count.toLocaleString()} events across {device.ip_count.toLocaleString()} IPs.</p>
        </div>
      </div>
      <KeyValueCard
        icon="devices"
        title="Device facts"
        rows={[
          ['alias', <span className="cid">{device.alias}</span>],
          ['device_id', <span className="cid">{device.device_id}</span>],
          ['site_key', device.site_key],
          ['first_seen', device.first_seen],
          ['last_seen', device.last_seen],
          ['event_count', device.event_count.toLocaleString()],
          ['ip_count', device.ip_count.toLocaleString()],
        ]}
      />
      <DataTable
        title="Recent events"
        icon="timeline"
        rows={device.recent_events}
        emptyTitle="No recent events"
        emptyMessage="Collect events for this device will appear here."
        columns={[
          { key: 'event_id', header: 'Event', className: 'cid' },
          { key: 'event_name', header: 'Name', render: (event) => <StatusChip value={event.event_name} tone="new" /> },
          { key: 'site_key', header: 'Site key' },
          { key: 'ip', header: 'IP' },
          { key: 'ts', header: 'Client time' },
          { key: 'server_ts', header: 'Server time' },
        ]}
      />
      <DataTable
        title="Recent decisions"
        icon="gavel"
        rows={device.recent_decisions}
        emptyTitle="No recent decisions"
        emptyMessage="Risk decisions tied to this device will appear here."
        columns={[
          { key: 'request_id', header: 'Request', className: 'cid' },
          { key: 'order_id', header: 'Order' },
          { key: 'arm', header: 'Arm', render: (decision) => <StatusChip value={decision.arm} tone="sub" /> },
          { key: 'recommendation', header: 'Verdict', render: (decision) => <StatusChip value={decision.recommendation} /> },
          { key: 'score', header: 'Score' },
          { key: 'ts', header: 'Time' },
        ]}
      />
      <DataTable
        title="Server events"
        icon="webhook"
        rows={device.server_events}
        emptyTitle="No server events"
        emptyMessage="Lifecycle events tied to this device will appear here."
        columns={[
          { key: 'order_id', header: 'Order', className: 'cid' },
          { key: 'request_id', header: 'Request', className: 'cid' },
          { key: 'event_name', header: 'Event', render: (event) => <StatusChip value={event.event_name} tone="ok" /> },
          { key: 'ts', header: 'Time' },
        ]}
      />
    </div>
  );
}
